/**
 * Vista pública del pagador (entra desde el enlace de WhatsApp).
 *
 * Diseño:
 *   - Header brand sólido con vertical + título + organizador
 *   - Tarjeta de progreso usando progressPhrase()
 *   - Detalle (importe / plazo)
 *   - Form mínimo (nombre, email, DNI con helper de validez legal)
 *   - Stepper de cantidad con total en vivo
 *   - Toggle Tarjeta/Bizum
 *   - CTA con importe
 *   - Línea final "Sin coste para ti"
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import { copyFor, progressPhrase } from '../utils/vertical-copy';


interface RaffleInfo {
  prize_title: string;
  prize_description?: string;
  prize_value: number;
  prize_image_url?: string;
  draw_date?: string;
  tickets_available: number;
  total_tickets: number;
}

interface GroupBuyPublic {
  id: string;
  slug: string;
  type: string;
  title: string;
  description?: string;
  cover_image_url?: string;
  organizer_name: string;
  amount_per_share?: string;
  amount_total_target?: string;
  expected_shares?: number;
  min_shares?: number;
  max_shares?: number;
  deadline?: string;
  status: string;
  paid_shares_count: number;
  total_paid_amount: string;
  allow_anonymous: boolean;
  require_dni: boolean;
  porra_event_id?: string | null;
  payment_provider?: 'pagafacil' | 'redsys_org';
  category_tiers?: { name: string; amount: number }[] | null;
}


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function GroupBuyPublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [gb, setGb] = useState<GroupBuyPublic | null>(null);
  const [raffle, setRaffle] = useState<RaffleInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dni, setDni] = useState('');
  const [shares, setShares] = useState(1);
  const [method, setMethod] = useState<'stripe' | 'bizum'>('stripe');
  const [categoryName, setCategoryName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/public/group-buys/${slug}`)
      .then((r) => {
        setGb(r.data);
        if (r.data.type === 'raffle_share') {
          api
            .get(`/public/group-buys/${slug}/raffle-info`)
            .then((rr) => setRaffle(rr.data))
            .catch(() => {});
        }
      })
      .catch(() => toast.error('No encontramos este cobro'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error('Pon tu nombre y email');
      return;
    }
    if (gb?.require_dni && !dni) {
      toast.error('Necesitas el DNI para que la papeleta quede a tu nombre');
      return;
    }
    if (gb?.category_tiers && gb.category_tiers.length > 0 && !categoryName) {
      toast.error('Elige una categoría');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/group-buys/${slug}/join`, {
        participant_name: name,
        participant_email: email,
        participant_phone: phone || undefined,
        participant_dni: dni || undefined,
        shares_count: shares,
        category_name: categoryName || undefined,
        payment_method: method,
      });
      toast.success('Te llevamos al pago…');
      window.location.href = data.payment_url;
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Algo no ha ido bien. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center font-sans text-ink-500">
        Un momento…
      </div>
    );

  if (!gb)
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center font-sans">
        <div className="max-w-sm">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Error
          </div>
          <h1 className="font-display font-bold text-[28px] text-ink mb-2 tracking-[-0.02em]">
            No encontramos este cobro
          </h1>
          <p className="text-ink-700 text-[14.5px]">¿El enlace está completo?</p>
        </div>
      </div>
    );

  const c = copyFor(gb.type);
  const amountPerShare = gb.amount_per_share ? parseFloat(gb.amount_per_share) : null;
  const totalPaid = parseFloat(gb.total_paid_amount);
  const progressPct = gb.expected_shares
    ? Math.min(100, (gb.paid_shares_count / gb.expected_shares) * 100)
    : 0;
  const isOpen = gb.status === 'open';
  const totalToPay = amountPerShare ? amountPerShare * shares : 0;
  const maxShares = Math.max(1, gb.max_shares || 99);

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      {/* Header brand sólido */}
      <header className="bg-brand text-paper">
        <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-paper/70 mb-3">
            · {c.shortLabel}
          </div>
          <h1 className="font-display font-bold text-[32px] md:text-[44px] leading-[1.02] tracking-[-0.02em] mb-3">
            {gb.title}
          </h1>
          <p className="text-paper/85 text-[14.5px]">
            Organiza: <span className="font-semibold text-paper">{gb.organizer_name}</span>
          </p>
        </div>
      </header>

      {gb.payment_provider === 'redsys_org' && (
        <div className="bg-olive-50 border-b border-[#C7D5A8]">
          <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-2">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-olive-700">
              · Tu pago va directo
            </span>
            <span className="text-[12.5px] text-ink-700">
              al banco de <strong>{gb.organizer_name}</strong> · Pagafácil no procesa este pago
            </span>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-12 grid lg:grid-cols-[1fr_1.05fr] gap-6 lg:gap-8">
        {/* Estado */}
        <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-2">
            · Cómo va
          </div>
          <p className="text-[18px] font-display font-semibold text-ink leading-tight tracking-[-0.01em] mb-1">
            {progressPhrase(gb.type, gb.paid_shares_count, gb.expected_shares)}
          </p>
          <p className="text-olive-700 font-semibold text-[14px] mb-5">
            Llevamos {formatEUR(totalPaid)}
          </p>

          {gb.expected_shares ? (
            <div className="h-2 bg-line rounded-pill overflow-hidden mb-6">
              <div
                className="h-full bg-olive rounded-pill transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          ) : null}

          <div className="border-t border-line-soft pt-5 space-y-3 text-[14px]">
            {amountPerShare && (
              <div className="flex items-center justify-between">
                <span className="text-ink-500">Cada {c.payerSingular}</span>
                <span className="font-display font-semibold text-[18px] text-ink">
                  {formatEUR(amountPerShare)}
                </span>
              </div>
            )}
            {gb.deadline && (
              <div className="flex items-center justify-between">
                <span className="text-ink-500">Hasta el</span>
                <span className="font-semibold text-ink">
                  {new Date(gb.deadline).toLocaleString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            )}
          </div>

          {gb.description && (
            <p className="mt-5 pt-5 border-t border-line-soft text-[14px] text-ink-700 leading-relaxed whitespace-pre-line">
              {gb.description}
            </p>
          )}

          {raffle && (
            <div className="mt-5 pt-5 border-t border-line-soft">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-terra-700 mb-2">
                · Premio en juego
              </div>
              <p className="font-display font-semibold text-[18px] text-ink tracking-[-0.01em] mb-1">
                {raffle.prize_title}
              </p>
              {raffle.prize_description && (
                <p className="text-[13.5px] text-ink-700 leading-relaxed mb-2">
                  {raffle.prize_description}
                </p>
              )}
              <p className="text-[13.5px] text-ink-700">
                Valor: <strong className="text-ink">{formatEUR(raffle.prize_value)}</strong>
              </p>
              {raffle.draw_date && (
                <p className="text-[12.5px] text-ink-500 mt-2">
                  Sorteo: {new Date(raffle.draw_date).toLocaleDateString('es-ES')}
                </p>
              )}
              <p className="text-[12.5px] text-ink-500 mt-1">
                {raffle.tickets_available}/{raffle.total_tickets} papeletas disponibles
              </p>
            </div>
          )}
        </section>

        {/* Form */}
        <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · {c.ctaJoin}
          </div>

          {!isOpen ? (
            <div className="bg-amberCC-50 border border-[#ECD9AE] rounded-xl p-4 text-amberCC-700 text-[14px]">
              {gb.status === 'completed' &&
                '✓ Este cobro ya está completo. Gracias por participar.'}
              {gb.status === 'expired' && 'El plazo ya pasó.'}
              {gb.status === 'cancelled' && 'El organizador canceló este cobro.'}
              {gb.status === 'settled' &&
                'Este cobro ya se cerró y el dinero llegó al organizador.'}
            </div>
          ) : (
            <form onSubmit={handleJoin} className="space-y-4">
              <Field label="Tu nombre" required>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="cc-input"
                  placeholder="Como aparecerá en tu justificante"
                />
              </Field>

              <Field label="Tu email" required>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="cc-input"
                  placeholder="Para recibir el justificante"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label={
                    <>
                      Teléfono <span className="text-ink-400 font-normal">(opcional)</span>
                    </>
                  }
                >
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="cc-input"
                    placeholder="+34 …"
                  />
                </Field>
                <Field
                  label={
                    <>
                      DNI {gb.require_dni && <span className="text-brick">*</span>}
                    </>
                  }
                  helper={
                    gb.require_dni
                      ? `Necesario para que la ${c.unitSingular} quede a tu nombre con validez legal.`
                      : undefined
                  }
                >
                  <input
                    type="text"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                    required={gb.require_dni}
                    className="cc-input"
                    placeholder="12345678A"
                  />
                </Field>
              </div>

              {/* Idea 12 · selector de categoría si hay tiers configurados */}
              {gb.category_tiers && gb.category_tiers.length > 0 && (
                <Field label="Categoría" required>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {gb.category_tiers.map((tier) => (
                      <button
                        key={tier.name}
                        type="button"
                        onClick={() => setCategoryName(tier.name)}
                        className={`px-3 py-2.5 rounded-xl border-[1.5px] text-left transition ${
                          categoryName === tier.name
                            ? 'border-brand bg-brand-50 text-brand-700'
                            : 'border-line-strong bg-paper text-ink-700 hover:border-brand'
                        }`}
                      >
                        <div className="font-display font-semibold text-[14.5px]">{tier.name}</div>
                        <div className="text-[12.5px] text-ink-500">
                          {tier.amount.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                        </div>
                      </button>
                    ))}
                  </div>
                </Field>
              )}

              {/* Stepper cantidad */}
              <div>
                <label className="cc-label">¿Cuántas {c.unitPlural}?</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShares((s) => Math.max(1, s - 1))}
                    aria-label="Quitar una"
                    className="w-11 h-11 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-xl font-bold hover:border-brand hover:text-brand transition disabled:opacity-50"
                    disabled={shares <= 1}
                  >
                    −
                  </button>
                  <div className="flex-1 h-11 rounded-xl border-[1.5px] border-line-strong bg-paper flex items-center justify-center font-display font-semibold text-[20px] text-ink">
                    {shares}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setShares((s) => Math.min(maxShares, s + 1))
                    }
                    aria-label="Sumar una"
                    className="w-11 h-11 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-xl font-bold hover:border-brand hover:text-brand transition disabled:opacity-50"
                    disabled={shares >= maxShares}
                  >
                    +
                  </button>
                </div>
                {amountPerShare && (
                  <p className="mt-2 text-[13px] text-ink-700">
                    Vas a pagar{' '}
                    <strong className="text-ink font-display text-[15px]">
                      {formatEUR(totalToPay)}
                    </strong>
                  </p>
                )}
              </div>

              {/* Método de pago */}
              <div>
                <label className="cc-label">¿Cómo pagas?</label>
                <div className="grid grid-cols-2 gap-2">
                  <SegBtn
                    selected={method === 'stripe'}
                    onClick={() => setMethod('stripe')}
                  >
                    💳 Tarjeta
                  </SegBtn>
                  <SegBtn
                    selected={method === 'bizum'}
                    onClick={() => setMethod('bizum')}
                  >
                    📱 Bizum
                  </SegBtn>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-2 px-4 py-3.5 rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition disabled:bg-[#EFE8DC] disabled:text-[#B2A89A] disabled:shadow-none disabled:cursor-not-allowed"
              >
                {submitting
                  ? 'Un momento…'
                  : amountPerShare
                  ? `Pagar ${formatEUR(totalToPay)}`
                  : c.ctaJoin}
              </button>
              <p className="text-[11.5px] text-ink-500 text-center">
                Sin coste para ti · Recibirás justificante por email
              </p>
            </form>
          )}
        </section>
      </main>

      <footer className="py-10 text-center text-[11.5px] text-ink-500 font-mono uppercase tracking-[0.14em]">
        Sin app · sin login · pago seguro con Stripe + Bizum
      </footer>

      {/* Estilos utilitarios privados (mantenemos Tailwind como capa principal) */}
      <style>{`
        .cc-input{
          width:100%;
          padding:13px 15px;
          border:1.5px solid #DDD3C4;
          border-radius:11px;
          background:#FFFFFF;
          font-family:inherit;
          font-size:14.5px;
          color:#26201A;
          outline:none;
          transition:border-color .15s ease;
        }
        .cc-input:focus{ border-color:#1C5D82; }
        .cc-input::placeholder{ color:#A89D8E; }
        .cc-label{
          display:block;
          font-family:"Hanken Grotesk",sans-serif;
          font-size:12.5px;
          font-weight:700;
          letter-spacing:.02em;
          color:#4A4239;
          margin-bottom:7px;
        }
        .cc-helper{
          margin-top:6px;
          font-size:12px;
          color:#8A8073;
          line-height:1.4;
        }
      `}</style>
    </div>
  );
}


// ─── Componentes auxiliares ────────────────────────────────────────


function Field({
  label,
  required,
  helper,
  children,
}: {
  label: React.ReactNode;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="cc-label">
        {label}
        {required && <span className="text-brick"> *</span>}
      </label>
      {children}
      {helper && <p className="cc-helper">{helper}</p>}
    </div>
  );
}


function SegBtn({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        selected
          ? 'px-3 py-2.5 rounded-xl text-[13.5px] font-semibold border-[1.5px] border-brand bg-brand-50 text-brand-700 transition'
          : 'px-3 py-2.5 rounded-xl text-[13.5px] font-semibold border-[1.5px] border-line-strong bg-paper text-ink-700 transition hover:border-brand'
      }
    >
      {children}
    </button>
  );
}
